import React from 'react';

import {selectElement, iframeCx} from "./wireframe-editor";
import Latch, {LatchController} from '../components/latch-button';

export default function TextEditor() {
	const getIframe = React.useContext(iframeCx)!;

	const abort = new AbortController();
	const latch = new LatchController();

	latch.bind({
		async onLatch() {
			const iframe = getIframe();

			const element = await selectElement(iframe, abort.signal);

			latch.unlatch();

			editTextFragment(iframe, element, abort);
		},

		onCancel() {
			abort.abort('cancel');
		},

		onEnd() {

		}
	})

	return <Latch controller={latch} data-icon={'\ue745'}>
		Textbaustein Bearbeiten
	</Latch>
}

function editTextFragment(iframe: HTMLIFrameElement, el: HTMLElement, abort: AbortController) {
	if (!el || el.matches('[contenteditable]')) return null;

	el.setAttribute('contenteditable', 'true');

	const doc = iframe.contentDocument!;

	doc.getSelection()?.removeAllRanges();
	doc.getSelection()?.selectAllChildren(el);

	const handlers = attachToolbar(iframe, el as HTMLElement);

	const editKeys = (e: KeyboardEvent) => {
		if (!e.shiftKey && e.key === 'Enter') {
			e.preventDefault();
			handlers.ok();

			(el as HTMLElement).removeEventListener('keydown', editKeys);
		} else if (e.key === 'Escape') {
			e.preventDefault();
			handlers.cancel();
			abort.abort();

			(el as HTMLElement).removeEventListener('keydown', editKeys);
		}
	};

	(el as HTMLElement).addEventListener('keydown', editKeys);

	// return {
	// 	cancel: () => {
	// 		handlers.cancel();
	// 		(el as HTMLElement).removeEventListener('keydown', editKeys);
	// 	},
	//
	// 	onCancel: handler => el.addEventListener('removeContenteditable', () => handler())
	// };
}

function clamp(val: number, min: number, max: number) {
	return Math.min(Math.max(val, min), max);
}

function attachToolbar(iframeEl: HTMLIFrameElement, target: HTMLElement) {
	const original = target.cloneNode(true);

	const parentDoc = iframeEl.ownerDocument;
	const parentWin = parentDoc.defaultView;

	const toolbar = parentDoc.createElement('div');
	toolbar.id = 'text-editor-toolbar';

	const confirmBtn = parentDoc.createElement('button');
	confirmBtn.dataset['icon'] = '\ue5ca';
	confirmBtn.textContent = 'Bestätigen';

	const resetBtn = parentDoc.createElement('button');
	resetBtn.dataset['icon'] = '\uf053';
	resetBtn.textContent = 'Abbrechen';

	toolbar.appendChild(confirmBtn);
	toolbar.appendChild(resetBtn);
	parentDoc.body.appendChild(toolbar);

	let rafId: number;

	const updatePosition = () => {
		const iframeRect = iframeEl.getBoundingClientRect();
		const elRect = target.getBoundingClientRect();

		const visible = elRect.bottom > 0 && elRect.top < iframeRect.height;
		toolbar.style.display = visible ? 'flex' : 'none';
		if (!visible) return;

		const toolbarHeight = toolbar.offsetHeight;
		const toolbarWidth = toolbar.offsetWidth;

		const rawTop = iframeRect.top + elRect.top - toolbarHeight - 8;
		const top = clamp(rawTop, iframeRect.top, iframeRect.bottom - toolbarHeight) - 4;

		const rawLeft = iframeRect.left + elRect.left;
		const left = clamp(rawLeft, iframeRect.left, iframeRect.right - toolbarWidth);

		toolbar.style.transform = `translate3d(${left}px, ${top}px, 0)`;
	};

	const loop = () => {
		updatePosition();
		rafId = requestAnimationFrame(loop);
	};

	parentWin?.addEventListener('scroll', updatePosition, true);
	parentWin?.addEventListener('resize', updatePosition);
	iframeEl.contentWindow?.addEventListener('scroll', updatePosition, true);

	rafId = requestAnimationFrame(loop);

	const cleanup = () => {
		cancelAnimationFrame(rafId);
		parentWin?.removeEventListener('scroll', updatePosition, true);
		parentWin?.removeEventListener('resize', updatePosition);
		iframeEl.contentWindow?.removeEventListener('scroll', updatePosition, true);
		toolbar.remove();

		target.dispatchEvent(new CustomEvent('removeContenteditable'));
	};

	const handlers = {
		ok: () => {
			target.removeAttribute('contenteditable');
			target.blur();
			cleanup();
		},
		cancel: () => {
			target.replaceWith(original);
			(original as HTMLElement).removeAttribute('contenteditable');
			target.blur();
			cleanup();
		}
	}

	confirmBtn.addEventListener('click', () => handlers.ok());
	resetBtn.addEventListener('click', () => handlers.cancel());

	return handlers;
}